import { message } from 'antd'
import axios from 'axios'
import AppConfig from '../config'
import apiClient from '../Utils/apiClient'
import { UserType } from './ActionType'
import { setSession, clearSession } from '../Utils/session'
import setAuthorizationToken from './setAuthorizationToken'

const persistSessionToken = (token, refreshToken) => {
  if (token) {
    setSession(token, refreshToken)
    setAuthorizationToken(token)
  }
}

const getApiErrorMessage = (err, fallback) => {
  const apiErrors = err.response?.data?.errors

  return (
    apiErrors?.err ||
    (apiErrors && Object.values(apiErrors)[0]) ||
    err.response?.data?.message ||
    fallback
  )
}

const { API_URL } = AppConfig

// const formDataHeader = {
//   Accept: 'application/x-www-form-urlencoded',
//   'Content-Type': 'application/x-www-form-urlencoded',
//   'cache-control': 'no-cache'
// }

const convertToQuery = (query) => {
  let url = ''

  if (query && Object.keys(query).length > 0) {
    const queryArr = Object.keys(query)
      .map((val) => `${val}=${query[val]}`)
      .join('&')
    url += `?${queryArr}`
  }

  return url
}

export const register = (data) =>
  axios
    .post(`${API_URL}/networks/register`, data)
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      message.error(res.data.message)

      return undefined
    })
    .catch(() => {
      message.error('Register failed')
    })

export const login = (data) =>
  function (dispatch) {
    return axios
      .post(`${API_URL}/auth`, data)
      .then((res) => {
        if (res.data?.token) {
          persistSessionToken(res.data.token, res.data.refreshToken)
          dispatch({ type: UserType.GET_USER, payload: res.data.result })
        }

        return res.data
      })
      .catch((err) => {
        message.error(
          err.response?.data?.message ||
            getApiErrorMessage(err, 'Invalid Credentials')
        )
      })
  }

export const validateLoginOTP = (data) =>
  function (dispatch) {
    return axios
      .post(`${API_URL}/auth/validate-login-otp`, data)
      .then((res) => {
        persistSessionToken(res.data.token, res.data.refreshToken)
        dispatch({ type: UserType.GET_USER, payload: res.data.result })

        return res.data
      })
      .catch((err) => {
        message.error(getApiErrorMessage(err, 'Invalid OTP'))
      })
  }

export const validateLoginMfa = (data) =>
  function (dispatch) {
    return axios
      .post(`${API_URL}/auth/validate-mfa`, data)
      .then((res) => {
        persistSessionToken(res.data.token, res.data.refreshToken)
        dispatch({ type: UserType.GET_USER, payload: res.data.result })

        return res.data
      })
      .catch((err) => {
        message.error(getApiErrorMessage(err, 'Invalid authenticator code'))
      })
  }

export const getMfaStatus = () =>
  axios
    .get(`${API_URL}/users/mfa/status`)
    .then((res) => (res.data.success ? res.data.result : { mfaEnabled: false }))
    .catch(() => ({ mfaEnabled: false }))

export const setupMfa = () =>
  axios
    .post(`${API_URL}/users/mfa/setup`)
    .then((res) => (res.data.success ? res.data.result : undefined))
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to start MFA setup'))

      return undefined
    })

export const enableMfa = (token) =>
  axios
    .post(`${API_URL}/users/mfa/enable`, { token })
    .then((res) => {
      if (res.data.success) {
        message.success('Authenticator app enabled')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to enable authenticator app'))

      return undefined
    })

export const disableMfa = (data) =>
  axios
    .post(`${API_URL}/users/mfa/disable`, data)
    .then((res) => {
      if (res.data.success) {
        message.success('Authenticator app disabled')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to disable authenticator app'))

      return undefined
    })

// ---------- User ---------- //

export const getUser = (data) =>
  axios
    .get(`${API_URL}/users/getAll`, data)
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      return undefined
    })
    .catch(() => {
      message.error('Getting users failed')
    })

export const getUserByToken = (token) =>
  function (dispatch) {
    return axios
      .get(`${API_URL}/users/byToken`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data.success) {
          setAuthorizationToken(token)
          dispatch({ type: UserType.GET_USER, payload: res.data.result })

          return res.data.result
        }

        return undefined
      })
      .catch(() => {
        clearSession()
        window.location.href = '/login'
      })
  }

export const addUser = (data) =>
  axios.post(`${API_URL}/users/add`, data).then((res) => {
    if (res.data.success || res.data.result) {
      return res.data
    }

    message.error(res.data.message)

    return undefined
  })

export const createUserByAdmin = (data) =>
  axios
    .post(`${API_URL}/users/admin/create`, data)
    .then((res) => {
      if (res.data.success) {
        if (!(res.data.temporaryPassword && !res.data.inviteEmailSent)) {
          message.success(res.data.message || 'User created')
        }

        return res.data
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to create user'))

      return undefined
    })

export const getManagedUsers = (query) => {
  const url = query
    ? `${API_URL}/users/manage${convertToQuery(query)}`
    : `${API_URL}/users/manage`

  return axios
    .get(url)
    .then((res) => {
      if (res.data.success) {
        return res.data
      }

      return { result: [], pageData: {} }
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load users'))

      return { result: [], pageData: {} }
    })
}

export const updateUserStorageLimit = (id, storageLimitGB) =>
  axios
    .put(`${API_URL}/users/${id}/storage-limit`, { storageLimitGB })
    .then((res) => {
      if (res.data.success) {
        message.success('Storage limit updated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update storage limit'))

      return undefined
    })

export const getMyStorageUsage = () =>
  axios
    .get(`${API_URL}/users/storage-usage`)
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      return undefined
    })
    .catch(() => undefined)

export const requestStorageIncrease = (data) =>
  apiClient
    .post('storage-requests', data)
    .then((res) => {
      if (res.data.success) {
        message.success(res.data.message || 'Storage request submitted')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to submit storage request'))

      return undefined
    })

export const getMyStorageRequest = () =>
  apiClient
    .get('storage-requests/me')
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      return undefined
    })
    .catch(() => undefined)

export const cancelMyStorageRequest = (id) =>
  apiClient
    .delete(`storage-requests/me/${id}`)
    .then((res) => {
      if (res.data.success) {
        message.success('Request cancelled')

        return true
      }

      return false
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to cancel request'))

      return false
    })

export const getStorageRequests = (status = 'pending') =>
  apiClient
    .get('storage-requests', { params: { status } })
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      return []
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load storage requests'))

      return []
    })

export const approveStorageRequest = (id, data = {}) =>
  apiClient
    .put(`storage-requests/${id}/approve`, data)
    .then((res) => {
      if (res.data.success) {
        message.success(res.data.message || 'Request approved')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to approve request'))

      return undefined
    })

export const rejectStorageRequest = (id, data = {}) =>
  apiClient
    .put(`storage-requests/${id}/reject`, data)
    .then((res) => {
      if (res.data.success) {
        message.success(res.data.message || 'Request rejected')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to reject request'))

      return undefined
    })

export const updateUserStatus = (id, status, organizationId) =>
  axios
    .put(`${API_URL}/users/${id}/status`, {
      status,
      ...(organizationId ? { organizationId } : {})
    })
    .then((res) => {
      if (res.data.success) {
        message.success(status === 'Active' ? 'User activated' : 'User deactivated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update user status'))

      return undefined
    })

export const getOrganizations = () =>
  apiClient
    .get('organizations')
    .then((res) => {
      if (res.data?.success) {
        return res.data.result || []
      }

      return []
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load organizations'))

      return []
    })

export const createOrganization = (data) =>
  apiClient
    .post('organizations', data)
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Organization created')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to create organization'))

      return undefined
    })

export const updateOrganizationSubscription = (id, data) =>
  apiClient
    .put(`organizations/${id}/subscription`, data)
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Subscription updated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update subscription'))

      return undefined
    })

export const setOrganizationStatus = (id, status, reason) =>
  apiClient
    .put(`organizations/${id}/status`, { status, reason })
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Organization updated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update organization status'))

      return undefined
    })

export const getInvoiceDetail = (id) =>
  apiClient
    .get(`billing/invoices/${id}`)
    .then((res) => (res.data?.success ? res.data.result : undefined))
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load invoice'))

      return undefined
    })

export const deleteInvoice = (id) =>
  apiClient
    .delete(`billing/invoices/${id}`)
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Invoice deleted')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to delete invoice'))

      return undefined
    })

export const generateSubscriptionInvoices = () =>
  apiClient
    .post('billing/generate-invoices')
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Invoices generated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to generate invoices'))

      return undefined
    })

export const assignUserOrganization = (id, organizationId) =>
  apiClient
    .put(`users/${id}/organization`, { organizationId })
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'User moved to organization')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to move user'))

      return undefined
    })

export const bulkAssignUserOrganization = (userIds, organizationId) =>
  apiClient
    .post('users/bulk/organization', { userIds, organizationId })
    .then((res) => {
      if (res.data?.success) {
        message.success(res.data.message || 'Users moved')

        return res.data
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Bulk move failed'))

      return undefined
    })

export const updateUserByAdmin = (id, data) =>
  axios
    .put(`${API_URL}/users/${id}/admin`, data)
    .then((res) => {
      if (res.data.success) {
        message.success('User updated')

        return res.data.result
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update user'))

      return undefined
    })

export const deleteUserByAdmin = (id) =>
  axios
    .delete(`${API_URL}/users/${id}`)
    .then((res) => {
      if (res.data.success) {
        message.success('User deleted')

        return true
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to delete user'))

      return undefined
    })

export const purgeUserByAdmin = (id) =>
  axios
    .post(`${API_URL}/users/${id}/purge`)
    .then((res) => {
      if (res.data.success) {
        message.success(res.data.message || 'User purged')

        return true
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to purge user'))

      return undefined
    })

export const resetUserMfaByAdmin = (id) =>
  axios
    .post(`${API_URL}/users/${id}/mfa/reset`)
    .then((res) => {
      if (res.data.success) {
        message.success(res.data.message || 'MFA reset')

        return res.data.result
      }

      if (res.data.message) {
        message.error(res.data.message)
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to reset MFA'))

      return undefined
    })

export const getAdminAudits = (query) => {
  const url = query
    ? `${API_URL}/admin-audits${convertToQuery(query)}`
    : `${API_URL}/admin-audits`

  return axios
    .get(url)
    .then((res) => {
      if (res.data.success) {
        return res.data
      }

      return { result: [], pageData: {} }
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load admin activity'))

      return { result: [], pageData: {} }
    })
}

export const getTrashItems = () =>
  apiClient
    .get('trash')
    .then((res) => {
      const payload = res?.data ?? res

      if (payload?.success === false) {
        return Promise.reject(new Error(payload?.message || 'Failed to load trash'))
      }

      const result = payload?.result

      if (Array.isArray(result)) {
        return { items: result, retentionDays: 30 }
      }

      return {
        items: result?.items || [],
        retentionDays: result?.retentionDays || 30
      }
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to load trash'))

      return Promise.reject(err)
    })

export const moveToTrash = (id) =>
  apiClient.post(`trash/${id}`).then((res) => {
    const payload = res?.data ?? res

    return payload?.success
  })

export const restoreFromTrash = (id) =>
  apiClient.put(`trash/${id}/restore`).then((res) => {
    const payload = res?.data ?? res

    return payload?.success
  })

export const restoreTrashBulk = (documentIds) =>
  apiClient.post('trash/restore-bulk', { documentIds }).then((res) => res?.data ?? res)

export const restoreAllTrash = () =>
  apiClient.post('trash/restore-all').then((res) => res?.data ?? res)

export const permanentDelete = (id) =>
  apiClient.delete(`trash/${id}/permanent`).then((res) => {
    const payload = res?.data ?? res

    return payload?.success
  })

export const emptyTrash = () =>
  apiClient.delete('trash/empty/all').then((res) => res?.data ?? res)

export const bulkUpdateUserStatus = (userIds, status, organizationId) =>
  apiClient
    .post('users/bulk/status', {
      userIds,
      status,
      ...(organizationId ? { organizationId } : {})
    })
    .then((res) => {
      if (res.data?.success || res.success) {
        message.success(res.data?.message || res.message || 'Users updated')

        return res.data || res
      }

      return undefined
    })
    .catch((err) => message.error(getApiErrorMessage(err, 'Bulk update failed')))

export const bulkDeleteUsers = (userIds, cleanupFiles = true) =>
  apiClient
    .post('users/bulk/delete', { userIds, cleanupFiles })
    .then((res) => {
      if (res.data?.success || res.success) {
        message.success(res.data?.message || res.message || 'Users deleted')

        return res.data || res
      }

      return undefined
    })
    .catch((err) => message.error(getApiErrorMessage(err, 'Bulk delete failed')))

export const updateUser = (id, data) =>
  function (dispatch, getState) {
    const isSelfUpdate = String(getState().users?.userInfo?.id) === String(id)

    return axios
      .put(`${API_URL}/users/update/${id}`, data)
      .then((res) => {
        if (res.data.success) {
          persistSessionToken(res.data.token)

          if (isSelfUpdate) {
            dispatch({ type: UserType.GET_USER, payload: res.data.result })
          }

          return res.data.result
        }

        if (res.data.message) {
          message.error(res.data.message)
        }

        return undefined
      })
      .catch((err) => {
        message.error(getApiErrorMessage(err, 'Profile update failed'))

        return undefined
      })
  }

export const updateUserEmailAsAdmin = (userId, email) =>
  axios
    .put(`${API_URL}/users/${userId}/update-email`, { email: email.trim() })
    .then((res) => {
      if (res.data.success) {
        return res.data.result
      }

      if (res.data.message) {
        message.error(res.data.message)
      }

      return undefined
    })
    .catch((err) => {
      message.error(getApiErrorMessage(err, 'Failed to update employee email'))

      return undefined
    })

export const changePassword = (id, data) =>
  axios.put(`${API_URL}/users/changePassword/${id}`, data).then((res) => {
    if (res.data.success) {
      return res.data
    }

    if (res.data.message) {
      message.error(res.data.message)
    }

    return undefined
  })
